import React, { useEffect, useState } from 'react';
import GetAppIcon from '@material-ui/icons/GetApp';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Loader, SuccessCheckmark } from '../../atoms';
import ModalDefaultDraggable from '../../molecules/ModalDefaultDraggable';
import { downloadInspectionReport } from '../../api/inspectionTypes';
import { handleMerge, runWithConcurrency } from './utils';
import { RootState } from '../../redux/root';
import { dispatch } from '../../redux/store';
import { setShowMergingForms } from '../../redux/slices/inspectionsSlice';
import { createNote } from '../../api/notes';

// How many report files to fetch in parallel. The backend's /inspections/download/
// returns a single file per call, so for a permit with 150+ inspections we still
// make one request each — but a small pool keeps it fast without flooding the API.
const DOWNLOAD_CONCURRENCY = 5;

const DownloadingPopup = () => {
  const { mergingFormsArray: inspections } = useSelector((s: RootState) => s.inspections);
  const total = inspections.length;

  const [showSuccess, setShowSuccess] = useState(false);
  const [link, setLink] = useState<HTMLAnchorElement>();
  const [downloadErr, setDownloadErr] = useState<string[]>([]);
  const [fatalError, setFatalError] = useState(false);
  const [downloaded, setDownloaded] = useState(0);
  const [started, setStarted] = useState(0);
  const [merging, setMerging] = useState(false);

  const close = () => dispatch(setShowMergingForms(false));

  useEffect(() => {
    const start = async () => {
      try {
        await downloadAllReports();
      } catch (e) {
        setFatalError(true);
      } finally {
        setShowSuccess(true);
      }
    };
    start();
  }, []);

  const saveReport = (): void => {
    if (link && link.download) link.click();
  };

  const downloadAllReports = async () => {
    const failed: string[] = [];

    const buffers = await runWithConcurrency(inspections, DOWNLOAD_CONCURRENCY, async insp => {
      setStarted(c => Math.min(c + 1, total));
      try {
        const result = await downloadInspectionReport(insp.id);
        const binaryString = atob(result.data.file);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let j = 0; j < len; j++) bytes[j] = binaryString.charCodeAt(j);
        return bytes.buffer as ArrayBuffer;
      } catch (e) {
        failed.push(insp.permitNumber || insp.projectName);
        return null;
      } finally {
        setDownloaded(c => c + 1);
      }
    });

    if (failed.length) setDownloadErr(failed);

    const dataArr = buffers.filter((b): b is ArrayBuffer => !!b);
    if (dataArr.length === 0) {
      setFatalError(true);
      return;
    }

    setMerging(true);
    const { permitNumber, projectName, permit } = inspections[0];
    const _link = await handleMerge(permitNumber || projectName, dataArr);
    setLink(_link);
    await createNote({ text: 'Inspection reports were downloaded', permit });
  };

  const mergedCount = total - downloadErr.length;
  const pct = total ? Math.round((downloaded / total) * 100) : 0;

  return (
    <ModalDefaultDraggable title="Merging Inspection Forms" onClose={close} narrow centered onClickAwayClose={false}>
      <Body>
        {!showSuccess && (
          <State>
            <Loader size={56} />
            {!merging ? (
              <>
                <Headline>Downloading inspection reports…</Headline>
                <Subtle>
                  Report {Math.min(Math.max(started, 1), total)} of {total}
                </Subtle>
                <ProgressTrack>
                  <ProgressBar style={{ width: `${pct}%` }} />
                </ProgressTrack>
              </>
            ) : (
              <>
                <Headline>Merging into one PDF…</Headline>
                <Subtle>
                  Combining {mergedCount} report{mergedCount === 1 ? '' : 's'}.
                </Subtle>
              </>
            )}
          </State>
        )}

        {showSuccess && !fatalError && (
          <State>
            <CheckWrapper>
              <SuccessCheckmark />
            </CheckWrapper>
            <Headline>Reports merged successfully</Headline>
            <Subtle>
              {mergedCount} inspection report{mergedCount === 1 ? '' : 's'} {mergedCount === 1 ? 'was' : 'were'} combined into one PDF.
            </Subtle>
            <PrimaryButton type="button" onClick={saveReport}>
              <GetAppIcon fontSize="small" />
              Download merged PDF
            </PrimaryButton>
          </State>
        )}

        {showSuccess && fatalError && (
          <State>
            <ErrorIcon />
            <Headline>Couldn’t merge the reports</Headline>
            <Subtle>None of the inspection reports could be downloaded. Please try again later.</Subtle>
          </State>
        )}

        {showSuccess && downloadErr.length > 0 && !fatalError && (
          <Callout>
            <ErrorOutlineIcon fontSize="small" />
            <span>
              Couldn’t download {downloadErr.length} report{downloadErr.length === 1 ? '' : 's'}: {downloadErr.join(', ')}. Please retry later.
            </span>
          </Callout>
        )}

        <Footer>
          <OutlineButton type="button" onClick={close} disabled={!showSuccess}>
            Close
          </OutlineButton>
        </Footer>
      </Body>
    </ModalDefaultDraggable>
  );
};

export default DownloadingPopup;

const fontStack = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const Body = styled.div`
  font-family: ${fontStack};
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const State = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 8px 0 4px;
`;

const CheckWrapper = styled.div`
  svg {
    width: 64px;
    height: 64px;
  }
`;

const ErrorIcon = styled(ErrorOutlineIcon)`
  &.MuiSvgIcon-root {
    width: 64px;
    height: 64px;
    fill: var(--danger-500);
  }
`;

const Headline = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
`;

const Subtle = styled.div`
  font-size: 0.875rem;
  color: var(--neutral-500);
  max-width: 320px;
`;

const ProgressTrack = styled.div`
  width: 240px;
  max-width: 100%;
  height: 6px;
  margin-top: 6px;
  border-radius: 999px;
  background: var(--neutral-100);
  overflow: hidden;
`;

const ProgressBar = styled.div`
  height: 100%;
  border-radius: 999px;
  background: var(--primary-600);
  transition: width 0.25s ease;
`;

const PrimaryButton = styled.button`
  margin-top: 8px;
  min-width: 200px;
  height: 40px;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: ${fontStack};
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const OutlineButton = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 18px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: ${fontStack};
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
  &:disabled {
    cursor: default;
    color: var(--neutral-400);
    background: var(--neutral-50);
  }
`;

const Callout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid var(--danger-100);
  border-radius: 8px;
  background: var(--danger-50);
  color: var(--danger-700);
  font-size: 0.8125rem;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  border-top: 1px solid var(--border);
  padding-top: 16px;
`;
